import { useEffect, useRef } from "react";

interface Props {
  /** The KVWB keurmerk id, e.g. "1712" */
  keurmerkId: string;
  /** Widget variant, e.g. "compact". Default "compact". */
  variant?: string;
  /** Widget theme, e.g. "dark" or "light". Default "dark". */
  theme?: string;
  className?: string;
}

/**
 * Lazy-loads the KVWB keurmerk widget once it approaches the viewport, so the
 * third-party script stays off the critical render path.
 */
export function KvwbKeurmerkWidget({
  keurmerkId,
  variant = "compact",
  theme = "dark",
  className,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let loaded = false;
    const load = () => {
      if (loaded || el.querySelector("script")) return;
      loaded = true;
      const script = document.createElement("script");
      script.src = `https://kvwb.nl/?kvwb_keurmerk_js=${encodeURIComponent(
        keurmerkId
      )}&variant=${encodeURIComponent(variant)}&theme=${encodeURIComponent(theme)}`;
      script.async = true;
      el.appendChild(script);
    };

    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            load();
            io.disconnect();
          }
        },
        { rootMargin: "300px" }
      );
      io.observe(el);
      return () => io.disconnect();
    }

    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [keurmerkId, variant, theme]);

  return (
    <div ref={ref} className={className}>
      <div data-kvwb-keurmerk={keurmerkId} />
    </div>
  );
}
